import { AppContext, UnityBridge } from "@Context/common/general/context";
import Event from "@Helpers/event";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ActiveVideo, ShowVideoOptions } from "./types";

const sendUnityEvent = (action: "enter" | "exit") => {
  UnityBridge.dispatchWebEvent({
    type: UnityBridge.OutgoingWebEvents.PresentationMode,
    payload: { action },
  });
};

export const useVideoDialog = () => {
  const { state } = React.useContext(AppContext);
  const exitEventRef = useRef<Event>();
  const exitSubscriptionRef = useRef<{ event: Event; handler: () => void }>();
  const onContinueRef = useRef<() => void>();
  const [video, setVideo] = useState<ActiveVideo>();

  const removeExitSubscription = useCallback(() => {
    const subscription = exitSubscriptionRef.current;
    subscription?.event.remove(subscription.handler);
    exitSubscriptionRef.current = undefined;
  }, []);

  const closeVideo = useCallback(
    (emitExitEvent: boolean) => {
      removeExitSubscription();

      const exitEvent = exitEventRef.current;
      exitEventRef.current = undefined;
      onContinueRef.current = undefined;
      setVideo(undefined);
      sendUnityEvent("exit");

      if (emitExitEvent) exitEvent?.emit();
    },
    [removeExitSubscription]
  );

  const hideVideo = useCallback(() => closeVideo(true), [closeVideo]);

  const showVideo = useCallback(
    ({ videoId, title, exitEvent, onContinue }: ShowVideoOptions) => {
      removeExitSubscription();
      exitEventRef.current = exitEvent;
      onContinueRef.current = onContinue;
      setVideo({ videoId, title, hasContinueAction: Boolean(onContinue) });
      sendUnityEvent("enter");

      if (exitEvent) {
        const handler = () => closeVideo(false);
        exitSubscriptionRef.current = { event: exitEvent, handler };
        exitEvent.on(handler);
      }
    },
    [closeVideo, removeExitSubscription]
  );

  const continueToChallenge = useCallback(() => {
    const onContinue = onContinueRef.current;
    hideVideo();
    onContinue?.();
  }, [hideVideo]);

  useEffect(() => {
    state.app.events.showVideo.on(showVideo);
    state.app.events.hideVideo.on(hideVideo);

    return () => {
      state.app.events.showVideo.remove(showVideo);
      state.app.events.hideVideo.remove(hideVideo);
      removeExitSubscription();
    };
  }, [hideVideo, removeExitSubscription, showVideo, state.app.events]);

  return { video, hideVideo, continueToChallenge };
};
